/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * checkInApi
 * ----------
 * Every request the check-in desk makes, and nothing else. Only two existing
 * endpoints are used:
 *
 *   POST /arcee/application/table/fetch/          one table, filtered — RLS applies,
 *                                                  so an organizer only ever sees
 *                                                  rows from their own events
 *   POST /arcee/application/table/save/records/   several writes, all-or-nothing
 *
 * /table/read/records/ would merge the reads into one request, but it applies
 * no row-level security, and RLS is what keeps one organizer's desk from
 * accepting another organizer's pass. So the reads stay on /table/fetch.
 *
 * Every function takes the APIHelper as its first argument so tests can pass a
 * fake one. A failed read throws a CheckInRequestError; callers decide what the
 * organizer sees.
 */

import envConfig from '../../../config/envConfig';
import { readLookup } from '../../athlohive/lookupOptions';
import { idOf, infoOf, nameOf } from './checkInRules';

export const PASS_TABLE = 'event_pass';
export const REGISTRATION_TABLE = 'event_registration';
export const CHECK_IN_TABLE = 'event_check_in';
export const CHECK_OUT_TABLE = 'event_check_out';
export const PASS_LOG_TABLE = 'event_pass_event_log';
export const ACTIVITY_TABLE = 'event_activity';

/** Saved analytics queries the History tab runs, by name. */
export const CAPACITY_QUERY = 'event_activity_capacity';
/** Every per-activity number in one round trip — capacity, registrations,
 *  check-ins, check-outs and expected revenue. Supersedes CAPACITY_QUERY for
 *  the History tab; the narrower one stays seeded as the simplest example. */
export const BREAKDOWN_QUERY = 'event_activity_breakdown';

/** A read or write that did not get an answer we can use. */
export class CheckInRequestError extends Error {
  constructor(message, { status = null, body = null } = {}) {
    super(message);
    this.name = 'CheckInRequestError';
    this.status = status;
    this.body = body;
  }
}

const equals = (column, value) => ({ column_key: column, filter_type: 'equals', column_value: value });
const within = (column, values) => ({ column_key: column, filter_type: 'in', column_value: values });

/**
 * One /table/fetch call → one page of rows plus the total that match
 * (`count`, the endpoint's COUNT(*) over the same filters).
 * `search` goes in the query string, where the endpoint reads it; it matches
 * the table's text columns (for passes: the passcode).
 */
export async function fetchPage(api, table, {
  filters = [], pageSize = 50, page = 1, ordering, search,
} = {}) {
  let resp;
  try {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    resp = await api.apiCall(`/arcee/application/table/fetch/${query}`, {
      method: 'POST',
      body: JSON.stringify({
        table_name: table,
        organization_id: Number(envConfig.organizationId),
        application_id: Number(envConfig.applicationId),
        filters,
        page_size: pageSize,
        page,
        ...(ordering ? { ordering } : {}),
      }),
    });
  } catch (error) {
    throw new CheckInRequestError(error?.message || 'No answer from the server.');
  }
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw new CheckInRequestError(`Reading ${table} failed.`, { status: resp.status, body });
  }
  const body = await resp.json();
  const rows = readLookup(body).rows;
  return { rows, count: Number.isFinite(body?.count) ? body.count : rows.length };
}

/** One /table/fetch call → its rows. */
export async function fetchRows(api, table, options) {
  return (await fetchPage(api, table, options)).rows;
}

/** COUNT(*) for the filters, without downloading the rows (a one-row page). */
export async function countRows(api, table, filters) {
  return (await fetchPage(api, table, { filters, pageSize: 1 })).count;
}

/** Every activity of the given events, in one call. */
export function fetchActivities(api, eventIds) {
  if (!eventIds.length) return Promise.resolve([]);
  return fetchRows(api, ACTIVITY_TABLE, { filters: [within('event_id', eventIds)], pageSize: 500 });
}

/**
 * Call 1 — whose pass is this?
 *
 * A QR holds the pass's `qr_token`, which is unique across every event, so it
 * needs no event filter. A typed passcode is only unique inside one event, so
 * it is searched in the events open for check-in; more than one row back means
 * the same code exists in two of them and the organizer has to say which.
 */
export function findPasses(api, { qrToken, passcode, eventIds }) {
  if (qrToken) {
    return fetchRows(api, PASS_TABLE, { filters: [equals('qr_token', qrToken)], pageSize: 2 });
  }
  if (!eventIds?.length) return Promise.resolve([]);
  return fetchRows(api, PASS_TABLE, {
    filters: [equals('passcode', passcode), within('event_id', eventIds)],
    pageSize: 10,
  });
}

/**
 * Call 2 — the pass's registration: still confirmed, and for which activity.
 *
 * Read by id through the single-record endpoint, because /table/fetch only
 * filters on the table's own columns and `id` is not one of them. That endpoint
 * applies the same row-level security: 404 when the row is gone, 403 when it is
 * not the organizer's — both mean "no usable registration".
 */
export async function fetchRegistration(api, registrationId) {
  let resp;
  try {
    resp = await api.apiCall(`/arcee/application/table/fetch/${encodeURIComponent(registrationId)}/`, {
      method: 'POST',
      body: JSON.stringify({
        table_name: REGISTRATION_TABLE,
        organization_id: Number(envConfig.organizationId),
        application_id: Number(envConfig.applicationId),
      }),
    });
  } catch (error) {
    throw new CheckInRequestError(error?.message || 'No answer from the server.');
  }
  if (resp.status === 404 || resp.status === 403) return null;
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw new CheckInRequestError('Reading the registration failed.', { status: resp.status, body });
  }
  return resp.json();
}

/** Call 3 — the check-in (or check-out) row for this pass, if there is one. */
export async function fetchCheckRow(api, table, passId) {
  const rows = await fetchRows(api, table, { filters: [equals('pass_id', passId)], pageSize: 1 });
  return rows[0] || null;
}

/**
 * Pass statuses for each list filter. A pass keeps only its latest status, so
 * "checked in" is CHECKED_IN or CHECKED_OUT — everyone who checked in,
 * including those who have since left. CANCELLED passes are never listed.
 */
export const PASS_STATUSES = {
  all: ['ISSUED', 'CHECKED_IN', 'CHECKED_OUT'],
  checked_in: ['CHECKED_IN', 'CHECKED_OUT'],
  checked_out: ['CHECKED_OUT'],
  not_arrived: ['ISSUED'],
};

/** How many passes of these events are in these statuses — one COUNT(*). */
export function countPasses(api, eventIds, statuses) {
  if (!eventIds.length) return Promise.resolve(0);
  return countRows(api, PASS_TABLE, [within('event_id', eventIds), within('status', statuses)]);
}

/**
 * How many check-ins (CHECK_IN_TABLE) or check-outs (CHECK_OUT_TABLE) these
 * events have — one COUNT(*) on that table. A pass has at most one of each
 * (UNIQUE pass_id), saved in the same batch as its status.
 */
export function countChecks(api, table, eventIds) {
  if (!eventIds.length) return Promise.resolve(0);
  return countRows(api, table, [within('event_id', eventIds)]);
}

/**
 * The pass filters every participant read and export shares, so the list, its
 * counts and the exported file can never disagree about who is in them.
 * `registrationIds` (optional) narrows to one activity's registrations.
 */
export function passFilters(eventIds, statuses, registrationIds) {
  const filters = [within('event_id', eventIds), within('status', statuses)];
  if (registrationIds) filters.push(within('registration_id', registrationIds));
  return filters;
}

/**
 * The ids of every CONFIRMED registration of one event, or of one activity of
 * it when `activityId` is given — one read.
 *
 * Cancelling a registration does not cancel its pass (only a refund moves
 * both), so a cancelled entry keeps an ISSUED pass and the pass status alone
 * would count it as Registered and Not arrived. Every History read is
 * therefore narrowed with `registration_id IN (these ids)`. A pass does not
 * carry its activity either; its registration does, which is how the same
 * read narrows to an activity. The same endpoint and the same row-level
 * security as every other read here.
 */
export async function fetchConfirmedRegistrationIds(api, eventId, activityId = null) {
  const filters = [equals('event_id', eventId), equals('status', 'CONFIRMED')];
  if (activityId != null) filters.push(equals('event_activity_id', activityId));
  const rows = await fetchRows(api, REGISTRATION_TABLE, { filters, pageSize: 25000 });
  return rows.map((row) => idOf(row.id)).filter((id) => id != null);
}

/** How many passes of these registrations are in these statuses — one COUNT(*). */
export function countActivityPasses(api, eventIds, statuses, registrationIds) {
  if (!eventIds.length || !registrationIds.length) return Promise.resolve(0);
  return countRows(api, PASS_TABLE, passFilters(eventIds, statuses, registrationIds));
}

/**
 * One page of passes, newest change first, as list rows. Everything a row
 * shows is on the pass itself (name from `user_info`, passcode, status, last
 * change), so a page is one request and nothing is downloaded beyond it.
 */
export async function fetchPassPage(api, {
  eventIds, statuses, search, registrationIds, page = 1, pageSize = 50,
}) {
  if (!eventIds.length) return { rows: [], count: 0 };
  // Narrowed to one activity: an activity nobody registered for has no passes.
  if (registrationIds && !registrationIds.length) return { rows: [], count: 0 };
  const result = await fetchPage(api, PASS_TABLE, {
    filters: passFilters(eventIds, statuses, registrationIds),
    ordering: '-updated_at',
    search: search || undefined,
    page,
    pageSize,
  });
  return {
    count: result.count,
    rows: result.rows.map((pass) => ({
      passId: pass.id,
      name: nameOf(pass.user_info),
      // Everything the pass knows about the person, for the details dialog.
      info: infoOf(pass.user_info),
      passcode: pass.passcode || '',
      eventId: idOf(pass.event_id),
      status: String(pass.status || '').toUpperCase(),
      changedAt: pass.updated_at,
    })),
  };
}

/**
 * First check-in and last check-out time for the given passes, from the pass
 * history — one read for a whole page of rows.
 */
export async function fetchPassTimes(api, passIds) {
  if (!passIds.length) return {};
  const log = await fetchRows(api, PASS_LOG_TABLE, {
    filters: [within('pass_id', passIds), within('event_type', ['CHECKED_IN', 'CHECKED_OUT'])],
    ordering: 'occurred_at',
    pageSize: passIds.length * 4,
  });
  const times = {};
  log.forEach((row) => {
    const entry = times[idOf(row.pass_id)] || (times[idOf(row.pass_id)] = { in: null, out: null });
    if (row.event_type === 'CHECKED_IN' && !entry.in) entry.in = row.occurred_at;
    if (row.event_type === 'CHECKED_OUT') entry.out = row.occurred_at;
  });
  return times;
}

/**
 * Call 4 — save the check-in or check-out.
 *
 * Three writes in one all-or-nothing batch: the check row, a line in the pass's
 * history, and the pass's status. `passId` and `eventId` always come from the
 * pass read in call 1, never from anything typed or chosen, so a save cannot
 * point at another event.
 */
export async function saveCheck(api, { mode, passId, eventId, userId, at = new Date() }) {
  const checkingIn = mode === 'in';
  const column = checkingIn ? 'checked_in' : 'checked_out';
  const status = checkingIn ? 'CHECKED_IN' : 'CHECKED_OUT';
  const when = at.toISOString();

  let resp;
  try {
    resp = await api.apiCall('/arcee/application/table/save/records/', {
      method: 'POST',
      body: JSON.stringify({
        organization_id: Number(envConfig.organizationId),
        application_id: Number(envConfig.applicationId),
        operations: [
          {
            table: checkingIn ? CHECK_IN_TABLE : CHECK_OUT_TABLE,
            data: {
              pass_id: passId,
              event_id: eventId,
              [`${column}_by_user_id`]: userId,
              [`${column}_at`]: when,
            },
          },
          {
            table: PASS_LOG_TABLE,
            data: {
              pass_id: passId,
              event_id: eventId,
              event_type: status,
              actor_user_id: userId,
              occurred_at: when,
            },
          },
          { table: PASS_TABLE, data: { id: passId, status } },
        ],
      }),
    });
  } catch (error) {
    // No answer at all: the save may or may not have landed.
    throw new CheckInRequestError(error?.message || 'No answer from the server.', { status: null });
  }
  const body = await resp.json().catch(() => null);
  if (!resp.ok) throw new CheckInRequestError('The save was refused.', { status: resp.status, body });
  return body;
}


/**
 * Run a saved analytics query by name.
 *
 * NOTHING ABOUT THE SQL CROSSES THE WIRE — only the query's name and its
 * declared params. The statement itself was written once by a developer, and
 * the server substitutes the row-level-security filter from the caller's own
 * session before it runs, read-only and under a timeout.
 *
 * The reply is a plain result set (columns, rows, meta), so the caller reads
 * its columns by name rather than assuming an order.
 */
export async function runAnalyticsQuery(api, name, params = {}) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  ).toString();
  const path = `/arcee/application/${Number(envConfig.applicationId)}`
    + `/analytics/${encodeURIComponent(name)}/run/${query ? `?${query}` : ''}`;

  let resp;
  try {
    resp = await api.apiCall(path, { method: 'GET' });
  } catch (error) {
    throw new CheckInRequestError(error?.message || 'No answer from the server.');
  }
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw new CheckInRequestError(`Running ${name} failed.`, { status: resp.status, body });
  }
  return resp.json();
}

/**
 * The columns of the participant export, in file order. Paths are read by the
 * export endpoint's `columns` mode: a json key on the pass, or FK hops to the
 * activity's name. qr_token is left out on purpose — it checks a person in.
 */
export const PARTICIPANT_EXPORT_COLUMNS = [
  { label: 'Name', path: ['user_info.first_name', 'user_info.last_name'] },
  { label: 'Email', path: 'user_info.email' },
  { label: 'Phone', path: ['user_info.country_code', 'user_info.mobile_number'] },
  // Filled only for a seat the booker named somebody else for.
  {
    label: 'Gender',
    path: 'user_info.gender',
    value_labels: { FEMALE: 'Female', MALE: 'Male', PREFER_NOT_TO_SAY: 'Prefer not to say' },
  },
  { label: 'Age', path: 'user_info.age' },
  { label: 'Passcode', path: 'passcode' },
  { label: 'Activity', path: 'registration_id.event_activity_id.name' },
  {
    label: 'Status',
    path: 'status',
    value_labels: { ISSUED: 'Not arrived', CHECKED_IN: 'Checked in', CHECKED_OUT: 'Checked out' },
  },
];

/** The export job's name on the server (tasks.TASK_EXPORT_TABLE_DATA). */
const EXPORT_TASK_NAME = 'Export of Table Data';

/**
 * Start an export of the participant list — the same filters as the list on
 * screen, so the file holds exactly its rows. Returns the job id.
 */
export async function requestParticipantExport(api, {
  eventIds, statuses, search, registrationIds, format, fileName,
}) {
  let resp;
  try {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    resp = await api.apiCall(`/arcee/application/table/export/${query}`, {
      method: 'POST',
      body: JSON.stringify({
        table_name: PASS_TABLE,
        organization_id: Number(envConfig.organizationId),
        application_id: Number(envConfig.applicationId),
        filters: passFilters(eventIds, statuses, registrationIds),
        format,
        ordering: '-updated_at',
        columns: PARTICIPANT_EXPORT_COLUMNS,
        // A readable name for the stored file; the server makes it safe.
        ...(fileName ? { file_name: fileName } : {}),
      }),
    });
  } catch (error) {
    // APIHelper answers a refused request (401/403 that is not a token or
    // session problem) by throwing `type: 'unauthorized'`, not with a response.
    const refused = error?.type === 'unauthorized' || error?.type === 'requires_auth';
    throw new CheckInRequestError(error?.message || 'No answer from the server.', {
      status: refused ? 403 : null,
    });
  }
  const body = await resp.json().catch(() => null);
  if (!resp.ok || body?.job_id == null) {
    throw new CheckInRequestError(body?.error || 'The export could not be started.', { status: resp.status, body });
  }
  return body.job_id;
}

/**
 * Where an export job is: 'submitted' | 'in_progress' | 'completed' | 'failed',
 * plus the file name once it is ready. The job list only ever holds the
 * caller's own jobs, newest first.
 */
export async function fetchExportJob(api, jobId) {
  const query = new URLSearchParams({
    organization_id: String(Number(envConfig.organizationId)),
    application_id: String(Number(envConfig.applicationId)),
    task_name: EXPORT_TASK_NAME,
    page_size: '25',
  }).toString();
  let resp;
  try {
    resp = await api.apiCall(`/arcee/application/jobs/list/?${query}`, { method: 'GET' });
  } catch (error) {
    throw new CheckInRequestError(error?.message || 'No answer from the server.');
  }
  if (!resp.ok) throw new CheckInRequestError('Reading the export status failed.', { status: resp.status });
  const body = await resp.json();
  const job = (body?.results || body?.records || []).find((row) => String(row.id) === String(jobId));
  if (!job) return { status: 'submitted', filename: null };
  let meta = job.metadata;
  if (typeof meta === 'string') {
    try { meta = JSON.parse(meta); } catch (error) { meta = {}; }
  }
  return { status: job.status, filename: meta?.filename || null };
}

/** The finished file, streamed by the download endpoint. */
export async function downloadExportFile(api, filename) {
  let resp;
  try {
    resp = await api.apiCall('/arcee/application/exports/download/', {
      method: 'POST',
      body: JSON.stringify({
        table_name: PASS_TABLE,
        organization_id: Number(envConfig.organizationId),
        application_id: Number(envConfig.applicationId),
        filename,
      }),
    });
  } catch (error) {
    throw new CheckInRequestError(error?.message || 'No answer from the server.');
  }
  if (!resp.ok) throw new CheckInRequestError('The file could not be downloaded.', { status: resp.status });
  return resp.blob();
}
